import * as React from 'react';

import { cn } from './lib/utils';
import { StatusDot } from './StatusDot';

/**
 * Compact data freshness indicator.
 *
 * Modes:
 *   <DataFreshnessChip updatedAt={new Date()} />
 *     → renders "Updated 5m ago" with a clock icon
 *     → automatically colours the dot: green <5m, yellow 5-30m, red >30m
 *
 *   <DataFreshnessChip label="Live" status="live" />
 *     → static label + StatusDot (for real-time feeds)
 *
 * className is applied to the outer span.
 */

function timeAgo(date) {
  const seconds = Math.floor((Date.now() - new Date(date).getTime()) / 1000);
  if (seconds < 60) return `${seconds}s ago`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
}

function freshnessStatus(date) {
  const minutes = (Date.now() - new Date(date).getTime()) / 60000;
  if (minutes < 5) return 'live';
  if (minutes < 30) return 'stale';
  return 'error';
}

function DataFreshnessChip({ updatedAt, label, status, className, ...props }) {
  const derived = updatedAt ? freshnessStatus(updatedAt) : (status ?? 'offline');
  const text = label ?? (updatedAt ? `Updated ${timeAgo(updatedAt)}` : null);

  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 text-[10px] text-text-muted leading-none',
        className
      )}
      {...props}
    >
      <StatusDot status={derived} size="sm" />
      {text && <span>{text}</span>}
    </span>
  );
}

export { DataFreshnessChip };
